/**
 * Button — the kit's five variants at one size (spec Decision 4): 28px, 12.5px/600, 6px radius,
 * 10px side padding. Every kit button carries data-kit="button" and data-variant (the
 * conformance test reads them). Icon-only buttons are Quiet and square: pass `icon` and an
 * aria-label. Destructive never acts on the first click — pair it with ConfirmModal.
 */
import * as React from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { disclosed, useDisclosureValue } from './disclosure';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'destructive' | 'arrowhead';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Icon-only: square, Quiet unless a variant is given. Needs an aria-label. */
  icon?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, icon, className, type = 'button', ...props },
  ref,
) {
  const v: ButtonVariant = variant ?? (icon ? 'quiet' : 'secondary');
  return (
    <button
      ref={ref}
      type={type}
      data-kit="button"
      data-variant={v}
      data-icon={icon ? 'true' : undefined}
      className={className ? `k-btn ${className}` : 'k-btn'}
      {...props}
    />
  );
});

export interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
}

/** A link that looks like a kit button; Arrowhead by default ("See the register"). */
export const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant = 'arrowhead', className, ...props },
  ref,
) {
  return <Link ref={ref} data-kit="button" data-variant={variant} className={className ? `k-btn ${className}` : 'k-btn'} {...props} />;
});

/**
 * Props that make any button-shaped element (a menu or popover trigger rendered by another
 * component) a kit button: spread onto the element.
 */
export function kitButtonProps(variant: ButtonVariant = 'secondary', icon = false) {
  return { 'data-kit': 'button', 'data-variant': variant, 'data-icon': icon ? 'true' : undefined, className: 'k-btn' } as const;
}

/**
 * An action group with its disabled-why line (spec: "A disabled action always says why. One line
 * under the action group, on every page"). `why` is shown whenever it is set; pass the reason for
 * the first disabled action, naming the record.
 */
export function ActionGroup({ children, why, className, elementId }: { children: React.ReactNode; why?: React.ReactNode; className?: string; elementId?: string }) {
  // T-087: an action group with a registry id is not rendered below its tier.
  if (!disclosed(useDisclosureValue(), elementId)) return null;
  return (
    <div className={className ? `k-actions ${className}` : 'k-actions'}>
      <div className="k-actions-row">{children}</div>
      {why ? <DisabledWhy>{why}</DisabledWhy> : null}
    </div>
  );
}

/** The one line under an action group that says why an action is disabled. */
export function DisabledWhy({ children, id, 'data-testid': testId }: { children: React.ReactNode; id?: string; 'data-testid'?: string }) {
  return (
    <p className="k-why" id={id} data-testid={testId} data-kit="disabled-why">
      {children}
    </p>
  );
}
